import { PreviewMessageType, type PreviewMessage } from '@webcontainer/api';
import type { UIMessage } from 'ai';
import type { StripeIntegration } from '~/lib/.server/llm/providers/types';
import type { ChatError } from '~/lib/errors';
import type { SupabaseChangeData } from '~/lib/stores/supabase';
import type { InspectedElement } from '~/lib/stores/workbench';

export interface FileSelection {
  filePath: string;
  text: string;
  range: {
    from: number;
    to: number;
  };
}

export interface FocusedFiles {
  lockedFiles: string[];
  targetedFiles: string[];
}

export interface SimpleAttachment {
  url: string;
  name?: string;
  contentType?: string;
}

export interface FileAttachment {
  id: string;
  file: File;
  uploading: boolean;
  storageKey: string;
}

export type ToolNames = 'getDeploymentStatus' | 'getEntriLinks' | 'str_replace_editor';

type Role = UIMessage['role'];

interface BaseMessage<T extends Role> extends UIMessage {
  messageId?: string;

  /**
   * Indicates if this message has a snapshot associated with it.
   */
  snapshot?: string;

  role: T;

  /**
   * Whether this message shouldn't be sent to the AI.
   */
  ignore?: boolean;

  /**
   * Whether this message should be hidden from the messages list.
   */
  hidden?: boolean;

  /**
   * Indicates whether this message is reversible.
   */
  allowUndo?: boolean;

  /**
   * Prevents merging this message with the previous message.
   */
  merge?: false;

  /**
   * Whether this message had an incomplete response or not.
   */
  status: (T extends 'user' ? 'unsent' : never) | 'streaming' | 'complete' | 'incomplete';

  error?: ChatError['data'];

  messageOrder?: number;
}

export interface UserMessage extends BaseMessage<'user'> {
  type: 'chat';

  /**
   * Set this property to true for user messages that we've
   * generated where you want the ability to handle more advanced
   * html.
   */
  renderHtml?: boolean;

  fileSelections?: FileSelection[];

  focusedFiles?: FocusedFiles;

  inspectedElement?: InspectedElement;

  stripeIntegration?: StripeIntegration;

  ephemeral?: boolean;

  /**
   * The input for the message to render to screen in messages view.
   *
   * @optional
   */
  input?: string;

  /**
   * Additional details about the message.
   */
  details?: Record<string, unknown>;
}

export interface SupabaseDBChangeMessage extends BaseMessage<'user'> {
  type: 'supabase';
  change: SupabaseChangeData;
  changeStatus: 'applied' | 'failed' | 'pending' | 'discarded';
}

export interface AssistantMessage extends BaseMessage<'assistant'> {
  type: 'chat';
  skipAction?: boolean;
}

// we don't use any of this type at the minute, but we can expand it in the future
interface OtherMessage extends BaseMessage<Exclude<Role, 'user' | 'assistant'>> {
  type: 'chat';
}

export const errorTypes = [
  PreviewMessageType.UncaughtException,
  PreviewMessageType.UnhandledRejection,
  PreviewMessageType.ConsoleError,
] as const;

interface BasePotentialProblem {
  id: string;
  count: number;
}

export interface PotentialShellProblem extends BasePotentialProblem {
  type: 'shell';
  exitCode?: number;
  command: string;
  output: string;
}

export interface PotentialTerminalProblem extends BasePotentialProblem {
  type: 'terminal';
  output: string;
}

export interface PotentialSupabaseMigrationProblem extends BasePotentialProblem {
  type: 'supabase-migration';
  filePath: string;
  query: string;
  message: string;
}

export interface PotentialSupabaseFunctionProblem extends BasePotentialProblem {
  type: 'supabase-function';
  functionSlug: string;
  message: string;
  requestUrl: string;
  status?: number;
  logs?: string[];
}

export type PreviewErrorMessage = Extract<PreviewMessage, { type: (typeof errorTypes)[number] }>;

export interface PotentialPreviewProblem extends BasePotentialProblem {
  type: 'preview';
  error: PreviewErrorMessage;
}

export type PotentialProblem =
  | PotentialPreviewProblem
  | PotentialShellProblem
  | PotentialSupabaseMigrationProblem
  | PotentialSupabaseFunctionProblem
  | PotentialTerminalProblem;

export interface PotentialProblemsMessage extends BaseMessage<'user'> {
  type: 'problems';
  problems: PotentialProblem[];
  resolved: boolean;

  // 'reasoning' would collide with the AI SDK's Message
  reasoningData?: ErrorReasoning;
}

interface ErrorFix {
  description: string;
  solution: string;
  filePaths: string[];
}

export interface ErrorReasoning {
  errorFixes: ErrorFix[];
}

export type Message =
  | UserMessage
  | AssistantMessage
  | PotentialProblemsMessage
  | OtherMessage
  | SupabaseDBChangeMessage;

export type ChatMessage = Extract<Message, { type: 'chat' }>;
